import React from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';

const Section = ({ number, title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {number}. {title}
        </Typography>
        {children}
    </Box>
);

const TermsAndConditionsDialog = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            {...APP_DIALOG}
        >
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight="bold">Terms and Conditions</Typography>
                    <Typography variant="caption">Last Updated: March 2026</Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Close">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Welcome to Vendora88. These Terms and Conditions ("Terms") govern your access to and use
                    of the Vendora88 platform, including the website https://vendora88.com and related services.
                    By accessing or using the platform, you agree to comply with and be bound by these Terms.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Section number={1} title="Ownership of the Platform">
                    <Typography variant="body2" paragraph>
                        Vendora88 is a technology platform owned and operated by <strong>Yolotech88 Pvt Ltd</strong>,
                        a company registered in India.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        The brand name <strong>"Vendora88"</strong>, the website https://vendora88.com, and all
                        associated software, systems, services, and communications are the intellectual property
                        of <strong>Yolotech88 Pvt Ltd</strong>.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Vendora88 provides digital tools that enable vendors to manage products, subscriptions,
                        deliveries, invoices, and payment tracking for their customers.
                    </Typography>
                    <Typography variant="body2">
                        All communications including OTP messages, service notifications, delivery reminders,
                        invoices, and system alerts sent under the name <strong>Vendora88</strong> are issued by
                        or on behalf of <strong>Yolotech88 Pvt Ltd</strong> for the purpose of providing services
                        through the platform.
                    </Typography>
                </Section>

                <Section number={2} title="Platform Services">
                    <Typography variant="body2" paragraph>
                        Vendora88 provides a cloud-based platform that allows vendors to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Create and manage products and subscription services',
                            'Add and manage customers',
                            'Generate invoices',
                            'Track deliveries and absentees',
                            'Record customer payments',
                            'Monitor dues and outstanding balances',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Vendora88 acts only as a technology platform and does not participate in the actual
                        sale or delivery of goods between vendors and customers.
                    </Typography>
                </Section>

                <Section number={3} title="Vendor Responsibilities">
                    <Typography variant="body2" paragraph>
                        Vendors using the platform are responsible for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Maintaining accurate product and pricing information',
                            'Recording deliveries and absences correctly',
                            'Maintaining customer records responsibly',
                            'Complying with all applicable laws and regulations',
                            'Managing their own billing and tax obligations',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Vendora88 is not responsible for disputes between vendors and their customers.
                    </Typography>
                </Section>

                <Section number={4} title="Customer Data">
                    <Typography variant="body2" paragraph>
                        Vendors may add customer details including name, phone number, and address for service
                        delivery purposes.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        By using Vendora88, vendors confirm that they have obtained the necessary consent
                        from their customers to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Store customer information',
                            'Send service notifications',
                            'Send OTP authentication messages',
                            'Send delivery and invoice updates',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Vendora88 processes this data solely for the operation of the platform.
                    </Typography>
                </Section>

                <Section number={5} title="Communication and Notifications">
                    <Typography variant="body2" paragraph>
                        Vendora88 may send service-related communications such as:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'OTP authentication messages',
                            'Delivery reminders',
                            'Invoice notifications',
                            'Payment confirmations',
                            'Account alerts',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        These communications may be sent through SMS, messaging platforms, or other electronic
                        communication channels.
                    </Typography>
                </Section>

                <Section number={6} title="Platform Availability">
                    <Typography variant="body2" paragraph>
                        Vendora88 aims to provide reliable service but does not guarantee uninterrupted access
                        to the platform.
                    </Typography>
                    <Typography variant="body2" paragraph>
                        Temporary interruptions may occur due to:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'System maintenance',
                            'Network issues',
                            'Infrastructure upgrades',
                            'Third-party service dependencies',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                </Section>

                <Section number={7} title="Limitation of Liability">
                    <Typography variant="body2" paragraph>
                        Yolotech88 Pvt Ltd shall not be liable for:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                        {[
                            'Disputes between vendors and customers',
                            'Loss of business or revenue',
                            'Incorrect data entered by users',
                            'Delivery failures or operational errors by vendors',
                        ].map((item) => (
                            <Typography component="li" variant="body2" key={item} sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Users are responsible for verifying all information recorded on the platform.
                    </Typography>
                </Section>

                <Section number={8} title="Account Security">
                    <Typography variant="body2" paragraph>
                        Users are responsible for maintaining the confidentiality of their login credentials
                        and OTP authentication codes.
                    </Typography>
                    <Typography variant="body2">
                        Vendora88 will not be responsible for unauthorized access resulting from user negligence.
                    </Typography>
                </Section>

                <Section number={9} title="Changes to the Terms">
                    <Typography variant="body2" paragraph>
                        Yolotech88 Pvt Ltd reserves the right to modify these Terms and Conditions at any time.
                        Updated versions will be posted on the website.
                    </Typography>
                    <Typography variant="body2">
                        Continued use of the platform after such changes constitutes acceptance of the revised Terms.
                    </Typography>
                </Section>

                <Section number={10} title="Governing Law">
                    <Typography variant="body2" paragraph>
                        These Terms shall be governed by and interpreted in accordance with the laws of India.
                    </Typography>
                    <Typography variant="body2">
                        Any disputes arising from the use of the platform shall be subject to the jurisdiction
                        of the courts in India.
                    </Typography>
                </Section>

                <Section number={11} title="Contact Information">
                    <Typography variant="body2">
                        For any questions regarding these Terms and Conditions, please contact:
                    </Typography>
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">Yolotech88 Pvt Ltd</Typography>
                        <Typography variant="body2">Website: https://vendora88.com</Typography>
                        <Typography variant="body2">Email: vendora88@yolotech88.com</Typography>
                    </Box>
                </Section>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button variant="contained" fullWidth onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TermsAndConditionsDialog;
